import { useState, useEffect, useRef } from 'react'
import menuApiService from '../services/menuApi.js'

// Custom hook for managing menus with API
export function useMenus(menuType = null) {
  const [menus, setMenus] = useState([])
  const [selectedMenu, setSelectedMenu] = useState(null)
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [loadingDetails, setLoadingDetails] = useState(false)
  const [switchingMenuType, setSwitchingMenuType] = useState(false)
  const [error, setError] = useState(null)
  const isInitialMount = useRef(true)

  // Load menu list from API (metadata only)
  const loadMenus = async (showLoading = true, isMenuTypeSwitch = false) => {
    try {
      if (showLoading) {
        setLoading(true)
      }
      if (isMenuTypeSwitch) {
        setSwitchingMenuType(true)
      }
      setError(null)
      const data = await menuApiService.getMenus(menuType)
      setMenus(data)
    } catch (err) {
      setError(err.message)
      console.error('Failed to load menus:', err)
    } finally {
      if (showLoading) {
        setLoading(false)
      }
      if (isMenuTypeSwitch) {
        setSwitchingMenuType(false)
      }
    }
  }

  // Load detailed menu with sections
  const loadMenuDetails = async (id) => {
    try {
      setLoadingDetails(true)
      const data = await menuApiService.getMenuDetails(id, menuType)
      setSelectedMenu(data)
      return data
    } catch (err) {
      setError(err.message)
      console.error('Failed to load menu details:', err)
      throw err
    } finally {
      setLoadingDetails(false)
    }
  }

  // Create a new menu
  const createMenu = async (menuData) => {
    try {
      const newMenu = await menuApiService.createMenu(menuData)
      setMenus(prev => [...prev, newMenu])
      return newMenu
    } catch (err) {
      setError(err.message)
      throw err
    }
  }

  // Update a menu (name, description)
  const updateMenu = async (id, menuData) => {
    try {
      const updatedMenu = await menuApiService.updateMenu(id, menuData)
      setMenus(prev => prev.map(m => m.id === id ? updatedMenu : m))
      return updatedMenu
    } catch (err) {
      setError(err.message)
      throw err
    }
  }

  // Update menu data (sections, items)
  const updateMenuData = async (id, menuData) => {
    try {
      const updatedMenu = await menuApiService.updateMenuData(id, menuData)
      // Update selected menu if it's the one being updated
      if (selectedMenu && selectedMenu.id === id) {
        setSelectedMenu(updatedMenu)
      }
      return updatedMenu
    } catch (err) {
      setError(err.message)
      throw err
    }
  }

  // Delete a menu
  const deleteMenu = async (id) => {
    try {
      await menuApiService.deleteMenu(id, menuType)
      setMenus(prev => prev.filter(m => m.id !== id))
      if (selectedMenu && selectedMenu.id === id) {
        setSelectedMenu(null)
      }
    } catch (err) {
      setError(err.message)
      throw err
    }
  }

  // Refresh menus without loading state (for manual refresh)
  const refreshMenusSilently = async () => {
    try {
      setRefreshing(true)
      setError(null)
      const data = await menuApiService.getMenus(menuType)
      setMenus(data)
    } catch (err) {
      setError(err.message)
      console.error('Failed to refresh menus:', err)
      throw err
    } finally {
      setRefreshing(false)
    }
  }

  // Load menus on component mount and when menu type changes
  useEffect(() => {
    // Only show loading on initial mount, not when switching menu types
    const shouldShowLoading = isInitialMount.current
    const isMenuTypeSwitch = !isInitialMount.current
    loadMenus(shouldShowLoading, isMenuTypeSwitch)
    isInitialMount.current = false
  }, [menuType])

  return {
    menus,
    selectedMenu,
    loading,
    refreshing,
    loadingDetails,
    switchingMenuType,
    error,
    createMenu,
    updateMenu,
    updateMenuData,
    deleteMenu,
    loadMenuDetails,
    refreshMenus: loadMenus,
    refreshMenusSilently
  }
}
